import { useTranslation } from 'react-i18next';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export interface LanguageInfo {
  code: string;
  label: string;
  flag: string;
}

export const languages: LanguageInfo[] = [
  { code: 'de', label: 'Deutsch', flag: '/flags/de.png' },
  { code: 'en', label: 'English', flag: '/flags/gb.png' },
  { code: 'fr', label: 'Français', flag: '/flags/fr.png' },
  { code: 'es', label: 'Español', flag: '/flags/es.png' },
  { code: 'it', label: 'Italiano', flag: '/flags/it.png' },
  { code: 'pl', label: 'Polski', flag: '/flags/pl.png' },
];

const flagSx = { width: 22, height: 16, objectFit: 'cover', borderRadius: '2px', border: '1px solid', borderColor: 'divider' } as const;

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'de';

  const handleChange = (e: SelectChangeEvent<string>) => {
    const lang = e.target.value;
    i18n.changeLanguage(lang);
    try { localStorage.setItem('pm_language', lang); } catch { /* noop */ }
  };

  return (
    <Select
      value={currentLang}
      onChange={handleChange}
      size="small"
      variant="outlined"
      renderValue={(value) => {
        const selected = languages.find((l) => l.code === value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="img" src={selected?.flag} alt={selected?.label} sx={flagSx} />
            <Typography variant="body2" fontWeight={500}>
              {value.toUpperCase()}
            </Typography>
          </Box>
        );
      }}
      sx={{ minWidth: 100, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.5 } }}
    >
      {languages.map((lang) => (
        <MenuItem key={lang.code} value={lang.code} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 2 }}>
          <Box component="img" src={lang.flag} alt={lang.label} sx={flagSx} />
          <Typography variant="body2" sx={{ flex: 1 }}>{lang.label}</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {lang.code.toUpperCase()}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  );
}
